<template>
  <div :class="embedded ? '' : 'card border-0 mb-3'">
    <div v-if="!embedded" class="card-header bg-white d-flex flex-wrap justify-content-between align-items-center py-2">
      <span class="font-weight-bold"><i class="bi bi-qr-code-scan mr-1 text-primary"></i>Scan member pass</span>
      <button class="btn btn-sm btn-outline-secondary" :disabled="!qrCodeId" @click="cameraOn ? stopCamera() : startCamera()">
        <i :class="cameraOn ? 'bi bi-camera-video-off' : 'bi bi-camera'"></i>
        {{ cameraOn ? 'Stop camera' : 'Use camera' }}
      </button>
    </div>
    <div :class="embedded ? '' : 'card-body'">
      <button
        v-if="embedded"
        class="btn btn-outline-dark btn-block mb-3"
        :disabled="!qrCodeId"
        @click="cameraOn ? stopCamera() : startCamera()"
      >
        <i :class="cameraOn ? 'bi bi-camera-video-off' : 'bi bi-camera'" class="mr-1"></i>
        {{ cameraOn ? 'Stop camera' : 'Scan with camera' }}
      </button>
      <div v-if="!qrCodeId" class="text-secondary small">Select an entrance first.</div>
      <template v-else>
        <div v-show="cameraOn" class="mb-3 position-relative bg-dark rounded overflow-hidden">
          <video ref="video" class="w-100 d-block" playsinline muted></video>
          <canvas ref="canvas" class="d-none"></canvas>
        </div>

        <div class="input-group">
          <input
            ref="input"
            v-model="code"
            type="text"
            class="form-control"
            placeholder="Scan with a barcode scanner or paste a pass code"
            autocomplete="off"
            @keyup.enter="submit(code)"
          />
          <div class="input-group-append">
            <button class="btn btn-primary" :disabled="busy || !code" @click="submit(code)">
              <span v-if="busy" class="spinner-border spinner-border-sm"></span>
              <span v-else>Check in</span>
            </button>
          </div>
        </div>
        <div v-if="cameraError" class="text-danger small mt-2">{{ cameraError }}</div>

        <div v-if="result" class="rounded p-3 mt-3 text-white" :class="result.ok ? 'bg-primary' : 'bg-danger'">
          <div class="d-flex align-items-center">
            <i :class="result.ok ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'" style="font-size:2rem;"></i>
            <div class="ml-3">
              <div class="h5 font-weight-bold mb-0">{{ result.name || 'Unknown pass' }}</div>
              <div>{{ result.message }}</div>
              <div v-if="result.detail" class="small">{{ result.detail }}</div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import jsQR from 'jsqr'

/**
 * Desk scanner for member passes. Works with the device camera (jsQR) and
 * with USB/Bluetooth barcode scanners, which simply "type" the code + Enter
 * into the focused input.
 */
export default {
  name: 'ScanPassBox',

  props: {
    qrCodeId: { type: String, default: '' },
    // Render without its own card/header (when placed inside another panel).
    embedded: { type: Boolean, default: false },
  },

  data() {
    return {
      code:        '',
      busy:        false,
      result:      null,
      cameraOn:    false,
      cameraError: '',
      stream:      null,
      frame:       null,
      lastCode:    '',
      lastAt:      0,
      clearTimer:  null,
    }
  },

  mounted() {
    this.focus()
  },

  beforeDestroy() {
    this.stopCamera()
    clearTimeout(this.clearTimer)
  },

  methods: {
    focus() {
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
    },

    async submit(raw) {
      const code = (raw || '').trim()
      if (!code || this.busy || !this.qrCodeId) return

      // The camera reads the same code many times a second - ignore repeats.
      const now = Date.now()
      if (code === this.lastCode && now - this.lastAt < 5000) return
      this.lastCode = code
      this.lastAt   = now

      this.busy = true
      try {
        const { data } = await axios.post('/api/subscriptions/checkin/attendant/scan', {
          code,
          qr_code_id: this.qrCodeId,
        })
        this.show(data)
        if (data.status === 'checked_in') this.$emit('checked-in', data)
      } catch (e) {
        const d = e.response?.data || {}
        this.show({ status: 'error', customer_name: d.customer_name, message: d.message || 'Could not check in.' })
      } finally {
        this.busy = false
        this.code = ''
        this.focus()
      }
    },

    show(data) {
      const ok  = data.status === 'checked_in'
      const sub = data.member && data.member.subscription
      this.result = {
        ok,
        name:    data.customer_name || (data.member && data.member.name),
        message: data.message || (ok ? 'Checked in' : 'Not allowed in'),
        detail:  sub ? `${sub.package} · until ${sub.end_date}` : '',
      }
      clearTimeout(this.clearTimer)
      this.clearTimer = setTimeout(() => { this.result = null }, 8000)
    },

    async startCamera() {
      this.cameraError = ''
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = 'This browser cannot use the camera. Use a barcode scanner instead.'
        return
      }
      try {
        this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        const video = this.$refs.video
        video.srcObject = this.stream
        await video.play()
        this.cameraOn = true
        this.tick()
      } catch (e) {
        this.cameraError = 'Camera permission was denied or no camera is available.'
      }
    },

    stopCamera() {
      if (this.frame) cancelAnimationFrame(this.frame)
      this.frame = null
      if (this.stream) this.stream.getTracks().forEach(t => t.stop())
      this.stream   = null
      this.cameraOn = false
    },

    tick() {
      const video  = this.$refs.video
      const canvas = this.$refs.canvas
      if (!this.cameraOn || !video || !canvas) return

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width  = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const qr  = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' })
        if (qr && qr.data) this.submit(qr.data)
      }
      this.frame = requestAnimationFrame(this.tick)
    },
  },
}
</script>
